import errorLoggerService from "@/app/errors/errorLoggerService";
import {
  AlcoholDutyFieldRequirement,
  BooleanFieldRequirement,
} from "@/app/services/generated/dutyrulesService";
import { EnumAlcoholType } from "@/app/services/generated/enums/enumAlcoholType.generated";
import { EnumDutyScheme } from "@/app/services/generated/enums/enumDutyScheme.generated";
import { EnumGermanBeerDutyMovementType } from "@/app/services/generated/enums/enumGermanBeerDutyMovementType.generated";
import type { EnumGermanBeerDutyProductType } from "@/app/services/generated/enums/enumGermanBeerDutyProductType.generated";
import type { EnumStandardOrder } from "@/app/services/generated/enums/enumStandardOrder.generated";
import type { EnumStockAdjustDutyImplication } from "@/app/services/generated/enums/enumStockAdjustDutyImplication.generated";
import type { StockLocation } from "@/app/services/generated/stocklocationService";
import type { Maybe } from "@/components/utils/typeHelpers";
import { useI18n } from "@/plugins/i18n";
import type { ValidationRule } from "@/plugins/validation";
import { useRuleSets } from "@/plugins/validation";
import dutyConfigurationStore from "@/store/modules/dutyConfigurationStore";
import userStore from "@/store/modules/userStore";
import type { ComputedRef, Ref } from "vue";
import { computed, ref } from "vue";

interface DutyRules {
  abv: AlcoholDutyFieldRequirement;
  manufacturer: AlcoholDutyFieldRequirement;
  unitDutiableLitres: AlcoholDutyFieldRequirement;
  plato: AlcoholDutyFieldRequirement;
  germanBeerDutyProductType: AlcoholDutyFieldRequirement;
  packageType: AlcoholDutyFieldRequirement;
}

interface DutyRuleSets {
  abv: ValidationRule[];
  manufacturer: ValidationRule[];
  unitDutiableLitres: ValidationRule[];
  plato: ValidationRule[];
  germanBeerDutyProductType: ValidationRule[];
  packageType: ValidationRule[];
}

const { ruleSet } = useRuleSets();
const { $m } = useI18n();

const defaultRequirement = new AlcoholDutyFieldRequirement();
defaultRequirement.required = false;
defaultRequirement.show = false;

const booleanRequirementDontShow = new BooleanFieldRequirement();
booleanRequirementDontShow.show = false;

export const useDutyRulesForLines = (
  alcoholType: Ref<EnumAlcoholType | undefined>
) => {
  const dutyRules = computed(() => {
    const requirements =
      dutyConfigurationStore.state.alcoholTypeToRequirementsMap.get(
        alcoholType.value ?? EnumAlcoholType.none
      );

    return {
      abv: requirements?.abv ?? defaultRequirement,

      manufacturer: requirements?.manufacturer ?? defaultRequirement,

      unitDutiableLitres:
        requirements?.unitDutiableLitres ?? defaultRequirement,

      plato: requirements?.plato ?? defaultRequirement,

      germanBeerDutyProductType:
        requirements?.germanBeerDutyProductType ?? defaultRequirement,

      finalUnitLitres: requirements?.finalUnitLitres ?? defaultRequirement,

      isBrewedForPersonalUse:
        requirements?.isBrewedForPersonalUse ?? defaultRequirement,

      isDesignedForPump: requirements?.isDesignedForPump ?? defaultRequirement,

      spiritDutyExemptionReason:
        requirements?.spiritDutyExemptionReason ?? defaultRequirement,

      packageType: requirements?.packageType ?? defaultRequirement,

      dateOfProductionDutyRate:
        requirements?.dateOfProductionDutyRate ?? defaultRequirement,
    };
  });

  const dutyRuleSets = computed(() => ({
    abv: dutyRules.value.abv.required ? [ruleSet.required()] : [],

    manufacturer: dutyRules.value.manufacturer.required
      ? [ruleSet.required()]
      : [],

    unitDutiableLitres: dutyRules.value.unitDutiableLitres.required
      ? [ruleSet.required(), ruleSet.notNegativeNumber(), ruleSet.notZero()]
      : [ruleSet.notNegativeNumber()],

    plato: dutyRules.value.plato.required ? [ruleSet.required()] : [],

    germanBeerDutyProductType: dutyRules.value.germanBeerDutyProductType
      .required
      ? [ruleSet.required()]
      : [],

    finalUnitLitres: dutyRules.value.finalUnitLitres.required
      ? [ruleSet.required(), ruleSet.notNegativeNumber(), ruleSet.notZero()]
      : [ruleSet.notNegativeNumber(), ruleSet.notZero()],

    spiritDutyExemptionReason: dutyRules.value.spiritDutyExemptionReason
      .required
      ? [ruleSet.required()]
      : [],

    packageType: dutyRules.value.packageType.required
      ? [ruleSet.required()]
      : [],
  }));

  const shouldShowDutyRow = computed(() =>
    Array.from(Object.values(dutyRules.value)).some((rule) => rule.show)
  );

  return {
    dutyRules,
    dutyRuleSets,
    shouldShowDutyRow,
  };
};

export const useDutyRulesForHeaders = () => {
  const dutyRules = computed(() => ({
    standardOrderType:
      dutyConfigurationStore.state.headerRequirements?.standardOrderType ??
      defaultRequirement,

    germanBeerDutyMovementType:
      dutyConfigurationStore.state.headerRequirements
        ?.germanBeerDutyMovementType ?? defaultRequirement,

    bondedStockLocation: {
      taxWarehouseCode:
        dutyConfigurationStore.state.stockLocationDutyRequirements
          ?.bondedRequirements.warehouseTaxCode ?? defaultRequirement,
    },

    nonBondedStockLocation: {
      taxWarehouseCode:
        dutyConfigurationStore.state.stockLocationDutyRequirements
          ?.nonBondedRequirements.warehouseTaxCode ?? defaultRequirement,
    },

    allowedAlcoholTypes: dutyConfigurationStore.state.allowedAlcoholTypes,

    stockAdjustmentDutyImplications:
      dutyConfigurationStore.state.stockAdjustmentDutyImplications,

    liquorLicenceNumber:
      dutyConfigurationStore.state.headerRequirements?.liquorLicenceNumber ??
      defaultRequirement,

    packageTypeCategory:
      dutyConfigurationStore.state.packageTypeDutyRequirements?.categoryType ??
      defaultRequirement,

    eireDutyMovementType:
      dutyConfigurationStore.state.headerRequirements?.eireDutyMovementType ??
      defaultRequirement,

    allowDraughtRelief:
      dutyConfigurationStore.state.headerRequirements?.allowDraughtRelief ??
      booleanRequirementDontShow,

    isOutletWholesaler:
      dutyConfigurationStore.state.outletDutyRequirements?.isWholesaler ??
      booleanRequirementDontShow,
  }));

  const dutyRuleSets = computed(() => ({
    standardOrderType: dutyRules.value.standardOrderType.required
      ? [ruleSet.required()]
      : [],

    germanBeerDutyMovementType: dutyRules.value.germanBeerDutyMovementType
      .required
      ? [ruleSet.required()]
      : [],

    bondedStockLocation: {
      taxWarehouseCode: dutyRules.value.bondedStockLocation.taxWarehouseCode
        .required
        ? [ruleSet.required()]
        : [],
    },

    nonBondedStockLocation: {
      taxWarehouseCode: dutyRules.value.nonBondedStockLocation.taxWarehouseCode
        .required
        ? [ruleSet.required()]
        : [],
    },
    packageTypeCategory: dutyRules.value.packageTypeCategory.required
      ? [ruleSet.required()]
      : [],

    eireDutyMovementType: dutyRules.value.eireDutyMovementType.required
      ? [ruleSet.required()]
      : [],
  }));

  // These don't revive as exact copies of the enum, so we need to evaluate against the value, not equality
  const dutyEnumFilters = computed(() => ({
    standardOrderType: (enumType: EnumStandardOrder) =>
      dutyConfigurationStore.state.headerRequirements?.allowedOrderTypes.some(
        (allowedType) => enumType.value === allowedType.value
      ),
    alcoholType: (enumType: EnumAlcoholType) =>
      dutyRules.value.allowedAlcoholTypes.some(
        (allowedType) => allowedType.value === enumType.value
      ),
    stockAdjustDutyImplication: (enumType: EnumStockAdjustDutyImplication) =>
      dutyRules.value.stockAdjustmentDutyImplications.some(
        (allowedType) => enumType.value === allowedType.value
      ),
  }));

  const visibleFieldsForCurrentDutyScheme = computed(() => {
    const allowedAlcoholTypes = dutyRules.value.allowedAlcoholTypes;
    return {
      isPlatoVisible: allowedAlcoholTypes.some(
        (type) => useDutyRulesForLines(ref(type)).dutyRules.value.plato.show
      ),
      isGermanBeerDutyProductTypeVisible: allowedAlcoholTypes.some(
        (type) =>
          useDutyRulesForLines(ref(type)).dutyRules.value
            .germanBeerDutyProductType.show
      ),
      isFinalUnitLitresVisible: allowedAlcoholTypes.some(
        (type) =>
          useDutyRulesForLines(ref(type)).dutyRules.value.finalUnitLitres.show
      ),
      isDesignedForPumpVisible: allowedAlcoholTypes.some(
        (type) =>
          useDutyRulesForLines(ref(type)).dutyRules.value.isDesignedForPump.show
      ),
      isBrewedForPersonalUseVisible: allowedAlcoholTypes.some(
        (type) =>
          useDutyRulesForLines(ref(type)).dutyRules.value.isBrewedForPersonalUse
            .show
      ),
      isSpiritDutyExemptionReasonVisible: allowedAlcoholTypes.some(
        (type) =>
          useDutyRulesForLines(ref(type)).dutyRules.value
            .spiritDutyExemptionReason.show
      ),

      isPackageTypeCategoryVisible: dutyRules.value.packageTypeCategory.show,

      isAllowDraughtReliefVisible: dutyRules.value.allowDraughtRelief.show,

      isOutletWholesalerVisible: dutyRules.value.isOutletWholesaler.show,
    };
  });

  return {
    dutyRules,
    dutyRuleSets,
    dutyEnumFilters,
    visibleFieldsForCurrentDutyScheme,
  };
};

/**
 * Displays german beer duty product type letter. Includes
 * german beer mixed product percentage separated by a pipe
 * if product type === M
 *
 * @example "M | 4%"
 *  "F"
 */
export const displayDutyCategoryWithMixPercentage = (
  germanBeerDutyProductType: EnumGermanBeerDutyProductType,
  germanBeerMixedProductPercentageA: number | null
): string => {
  const categoryLetter = germanBeerDutyProductType.translation;

  return germanBeerDutyProductType.isMixedProduct_M()
    ? `${categoryLetter} | ${germanBeerMixedProductPercentageA}%`
    : categoryLetter;
};

/**
 * Validation ruleset for the german duty movement type field
 * on stock transfers. This follows same validation logic as the
 * `SaveStockTransferAsync` method in StockTransferService
 */
export const germanDutyMovementTypeEnumRuleSetForStockTransfers = (
  fromStockLocation: ComputedRef<Maybe<StockLocation>>,
  toStockLocation: ComputedRef<Maybe<StockLocation>>
): ComputedRef<ValidationRule> =>
  computed(() => {
    // these rulesets only apply if german beer duty movement is shown
    if (
      !useDutyRulesForHeaders().dutyRules.value.germanBeerDutyMovementType.show
    ) {
      return () => true;
    }

    if (!fromStockLocation.value || !toStockLocation.value) {
      return () => false;
    }

    // From external should ALWAYS be a NONE movement
    if (fromStockLocation.value.isExternal) {
      return ruleSet.containedInArray(
        [EnumGermanBeerDutyMovementType.none].map((arr) => arr.key),
        $m.german_duty_transfer_from_external
      );
    }

    // Bonded to bonded
    if (fromStockLocation.value.isBonded && toStockLocation.value.isBonded) {
      return ruleSet.containedInArray(
        [
          EnumGermanBeerDutyMovementType.dutySuspendedRemovalStayingInGermany,
          EnumGermanBeerDutyMovementType.dutyExemptRemovalGoingToEU,
        ].map((arr) => arr.key),
        $m.german_duty_transfer_taxed_taxed
      );
    }

    // Bonded to non-bonded
    if (fromStockLocation.value.isBonded && !toStockLocation.value.isBonded) {
      return ruleSet.containedInArray(
        [EnumGermanBeerDutyMovementType.dutiableRemoval].map((arr) => arr.key),
        $m.german_duty_transfer_taxed_non_taxed
      );
    }

    // Non-bonded to bonded
    if (!fromStockLocation.value.isBonded && toStockLocation.value.isBonded) {
      return ruleSet.containedInArray(
        [EnumGermanBeerDutyMovementType.none].map((arr) => arr.key),
        $m.german_duty_transfer_non_taxed_taxed
      );
    }

    // Non-bonded to non-bonded
    if (!fromStockLocation.value.isBonded && !toStockLocation.value.isBonded) {
      return ruleSet.containedInArray(
        [EnumGermanBeerDutyMovementType.none].map((arr) => arr.key),
        $m.german_duty_transfer_non_taxed_non_taxed
      );
    }

    return () => false;
  });

export class DutyConfiguration {
  constructor(public alcoholType?: EnumAlcoholType | undefined) {}

  get dutyConfigForLines() {
    const dr = useDutyRulesForLines(computed(() => this.alcoholType));
    return {
      dutyRules: dr.dutyRules.value,
      dutyRuleSets: dr.dutyRuleSets.value,
      shouldShowDutyRow: dr.shouldShowDutyRow.value,
    };
  }

  get dutyConfigForHeaders() {
    const dr = useDutyRulesForHeaders();
    return {
      dutyRules: dr.dutyRules.value,
      dutyRuleSets: dr.dutyRuleSets.value,
      visibleFieldsForCurrentDutyScheme:
        dr.visibleFieldsForCurrentDutyScheme.value,
      dutyEnumFilters: dr.dutyEnumFilters.value,
    };
  }
}

export const dutySchemeType = computed(() => {
  // @TODO: [BREW-5797] Temporary FE implementation - Remove for BE provided value when available
  const countryCode =
    userStore.state.tenant?.locationConfiguration.locationCountryCode;
  switch (countryCode) {
    case "AU":
      return EnumDutyScheme.australia;
    case "GB":
    case "UK":
      return EnumDutyScheme.uk;
    case "DE":
      return EnumDutyScheme.germany;
    case "ZA":
      return EnumDutyScheme.southAfrica;
    case "SE":
      return EnumDutyScheme.sweden;
    case "US":
      return EnumDutyScheme.us;
    case "IE":
      return EnumDutyScheme.eire;
    case "NZ":
      return EnumDutyScheme.nz;
    default:
      errorLoggerService.logError(
        `Unknown duty scheme type from country code: "${countryCode}"`
      );
      return EnumDutyScheme.none;
  }
});
